import { useFormik } from "formik";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory, useParams, withRouter } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { object, string } from "yup";

import AsyncSelectField from "../../../Components/Common/AsyncSelectField";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Loader from "../../../Components/Common/Loader";
import {
  useUpdateUserSubcategory,
  useUserCategories,
  useUserSubcategory,
} from "../../../hooks/useUserCategories";

const UpdateSubCategory = (props) => {
  document.title = props.t("Modifier une institution | Le Conseiller du BTP");

  const history = useHistory();
  const id = useParams().id;

  const subcategoryQ = useUserSubcategory(id);
  const subcategory = subcategoryQ.data || {};
  const categoriesQ = useUserCategories();
  const categories = categoriesQ.data || [];
  const updateMutation = useUpdateUserSubcategory();

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: subcategory.name || "",
      category_id: subcategory.category_id
        ? String(subcategory.category_id)
        : "",
    },

    validationSchema: object().shape({
      name: string().required(props.t("Entrer le nom de l'institution")),
      category_id: string().required(
        props.t("Choisissez la catégorie d'institutions")
      ),
    }),

    onSubmit: async (values) => {
      try {
        await updateMutation.mutateAsync({
          id,
          payload: {
            name: values.name,
            category_id: Number(values.category_id),
          },
        });
        history.push("/utilisateurs-institutions");
      } catch (_) {
        // handled by toast in hook
      }
    },
  });

  // Wait for the categories too: with `enableReinitialize`, if the institution
  // resolves first the matching <option> does not exist yet and the browser
  // falls back to the placeholder, so the form opens with no category selected.
  if (subcategoryQ.isLoading || categoriesQ.isLoading) return <Loader />;

  if (subcategoryQ.isError) {
    return (
      <Loader
        error={
          subcategoryQ.error?.message ||
          props.t("Impossible de charger cette institution")
        }
      />
    );
  }

  const usersCount = subcategory.users_count || 0;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Modifier une institution")}
          pageTitle={props.t("Utilisateurs")}
        />

        <Row>
          <Col lg={12}>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Card>
                <CardBody>
                  {usersCount > 0 ? (
                    <div className="alert alert-info" role="alert">
                      <i className="ri-information-line align-bottom me-1"></i>
                      {usersCount} utilisateur{usersCount > 1 ? "s" : ""}{" "}
                      {usersCount > 1 ? "sont rattachés" : "est rattaché"} à
                      cette institution. Changer sa catégorie les déplacera
                      également.
                    </div>
                  ) : null}

                  <div className="mb-3">
                    <Label className="form-label" htmlFor="name">
                      {props.t("Nom de l'institution")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      className="form-control"
                      placeholder={props.t("Ex : Ministère de la Justice")}
                      name="name"
                      value={validation.values.name}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      invalid={Boolean(
                        validation.errors.name && validation.touched.name
                      )}
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <AsyncSelectField
                    label={props.t("Catégorie d'institutions")}
                    name="category_id"
                    value={validation.values.category_id}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    options={categories}
                    optionLabelKey="name"
                    placeholder={props.t("Sélectionner la catégorie")}
                    required
                    isLoading={categoriesQ.isLoading}
                    isError={categoriesQ.isError}
                    onRetry={categoriesQ.refetch}
                    errorText={props.t(
                      "Impossible de charger les catégories d'institutions."
                    )}
                    invalid={Boolean(
                      validation.errors.category_id &&
                        validation.touched.category_id
                    )}
                    feedback={validation.errors.category_id}
                  />
                </CardBody>
              </Card>

              <div className="text-end mb-3">
                <Link
                  to="/utilisateurs-institutions"
                  className="btn btn-primary me-2"
                >
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning w-sm m-2"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending && (
                    <Spinner size="sm" className="me-2" />
                  )}
                  {props.t("Modifier")}
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

UpdateSubCategory.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(UpdateSubCategory));
